/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import { Paper, Checkbox } from "@material-ui/core";
import { AllInclusive } from "@material-ui/icons";
import { useHistory, Link } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import React, { useState, useEffect } from "react";
import CustomPagination from "../components/CustomPagination";
import {
  Button,
  Col,
  Container,
  Row,
  Table,
  Card,
  CardHeader,
  Spinner,
  Alert,
  CardFooter,
} from "reactstrap";
import Toastr from "toastr";
import { DateTime } from "luxon";
import UploadDropzone from "../components/UploadDropzone";
import { useQueryParams } from "../hooks/useQueryParams";
import { useSaveImageIntellMutation } from "../hooks/useSaveImageIntellMutation";
import { useGetImageIntelExecutions } from "../hooks/useGetImageIntelExecutions";
import { toCustomDateTimeString } from "../utils/toCustomDateTimeString";

const RESULTS_PER_PAGE = 10;

const ImageIntel: React.FC = () => {
  const query = useQueryParams();
  const router = useHistory();
  const [inputFiles, setInputFiles] = useState<File[]>([]);
  const [errorImages, setErrorImages] = useState<File[]>([]);
  const [selectedEngineType, setSelectedEngineType] = useState("CLAUDE");

  useEffect(() => {
    if (inputFiles.length > 0) {
      setErrorImages([]);
      for (let fileIndex in inputFiles) {
        let img = new Image();
        var objectUrl = window.URL.createObjectURL(inputFiles[fileIndex]);

        img.src = objectUrl;

        img.onload = () => {
          const width = img.width;
          const height = img.height;

          if (height > 2000 || width > 2000) {
            Toastr.error(
              `Dimension of File (${inputFiles[fileIndex].name}) is ${height} X ${width}, we currently does not support pixels more than 2000 x 2000.`,
              "Validation Error: ",
              { timeOut: 1000 * 60, extendedTimeOut: 1000 * 60 }
            );

            let images = [...errorImages, inputFiles[fileIndex]];

            setErrorImages(images);
          }
        };
      }
    }
  }, [inputFiles]);

  const [pageTable, setPageTable] = useState(() => {
    let page = +(query.get("page") || 0);
    if (isNaN(page) || page <= 0) {
      return 1;
    }
    return page;
  });

  const { isError, isLoading, data, refetch } = useGetImageIntelExecutions(
    pageTable,
    RESULTS_PER_PAGE
  );

  useEffect(() => {
    refetch();
  }, [pageTable]);

  let content: JSX.Element | null = null;

  if (isError) {
    content = <Alert color="danger">Something went wrong!</Alert>;
  } else if (isLoading) {
    content = <Spinner />;
  } else if (!data || data.data.length === 0) {
    content = <Alert color="warning">No data available</Alert>;
  } else {
    content = (
      <>
        <Table className="align-items-center table-flush" responsive>
          <thead className="thead-light">
            <th>Execution ID</th>
            <th>Images Count</th>
            <th>Execution By</th>
            <th>Executed At</th>
          </thead>
          <tbody>
            {data.data.map((execution, i) => (
              <>
                <tr key={i}>
                  <td>
                    <Link to={`/admin/image-intel/${execution.id}`}>
                      {execution.id}
                    </Link>
                  </td>
                  <td>{execution.images.length}</td>
                  <td>{execution.userName}</td>
                  <td>
                    {execution.createdAt
                      ? toCustomDateTimeString(
                          DateTime.fromISO(execution.createdAt)
                        )
                      : "NA"}
                  </td>
                </tr>
              </>
            ))}
          </tbody>
        </Table>
        <CardFooter className="py-4" style={{ textAlign: "center" }}>
          <nav style={{ display: "inline-block" }} aria-label="...">
            <CustomPagination
              activePage={pageTable}
              totalResults={data.total}
              resultsPerPage={RESULTS_PER_PAGE}
              onChange={setPageTable}
              label="Table navigation"
            />
          </nav>
        </CardFooter>
      </>
    );
  }

  const {
    mutate: processImages,
    data: saveProcessImagesResponse,
    isLoading: saveProcessImagesLoading,
    isError: saveProcessImagesError,
  } = useSaveImageIntellMutation();

  useEffect(() => {
    if (saveProcessImagesResponse && saveProcessImagesResponse.id) {
      router.push(`/admin/image-intel/${saveProcessImagesResponse.id}`);
    }
  }, [saveProcessImagesResponse, router]);

  useEffect(() => {
    if (saveProcessImagesError) {
      Toastr.error("Something bad happened.");
    }
  }, [saveProcessImagesError]);

  const handleSubmit = async () => {
    if (!inputFiles || inputFiles.length < 1) {
      Toastr.error("Please upload atleast 1 image to proceed further!");
      return;
    }

    if (inputFiles.length > 5) {
      Toastr.error("Max 5 images are supported.");
      return;
    }

    if (!prompt) {
      Toastr.error("Please enter a prompt");
      return;
    }

    if (errorImages.length === 0) {
      processImages({ files: inputFiles,engine_type: selectedEngineType });
    } else {
      Toastr.error(`Reupload the files.`);
    }
  };
  const handleCheckboxChange = (option: string) => {
    setSelectedEngineType(option);
  };

  return (
    <>
      <div
        className="header pb-6 pt-5 pt-md-8"
        style={{
          height: "500px",
          backgroundColor: "#2D315C",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundImage: "url(/background-header-infinity.jpg)",
        }}
      >
        {/* Mask */}
        <span className="mask bg-gradient-default opacity-8" />
        <Container style={{ display: "flex", flexDirection: "column" }} fluid>
          <Row>
            <Col>
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    display: "inline-block",
                    width: "75%",
                  }}
                >
                  <UploadDropzone
                    isFileUploaded={!!saveProcessImagesLoading}
                    setFiles={setInputFiles}
                    accept={[
                      "image/jpeg",
                      "image/png",
                      "image/gif",
                      "image/webp",
                    ]}
                    icon="/file-type-image.png"
                    title="Click/drag and drop image file(s)."
                  />
                </div>
                <div
                  style={{
                    display: "inline-block",
                    width: "75%",
                  }}
                >
                  <Checkbox
                    checked={selectedEngineType === "CLAUDE"}
                    onChange={() => handleCheckboxChange("CLAUDE")}
                    name="engine_type"
                    // onChange={(e) => setIsConfidential(e.target.checked)}
                    inputProps={{ "aria-label": "primary checkbox" }}
                    style={{ color: "#fff" }}
                  />
                  <label style={{ color: "white" }}>Model A</label>
                  
                  <Checkbox
                    checked={selectedEngineType === "GPT4O"}
                    onChange={() => handleCheckboxChange("GPT4O")}
                    name="engine_type"
                    // onChange={(e) => setIsConfidential(e.target.checked)}
                    inputProps={{ "aria-label": "primary checkbox" }}
                    style={{ color: "#fff" }}
                  />
                  <label style={{ color: "white" }}>Model B</label>
                </div>
                {selectedEngineType === "CLAUDE" && <label style={{ color: "white" }}>Warning: Model A is not suitable for client confidential information. For sensitive data, please select Model B</label>}
              </div>
              <div className="text-center">
                {saveProcessImagesLoading ? (
                  <svg
                    height="70px"
                    viewBox="0 0 187.3 93.7"
                    preserveAspectRatio="xMidYMid meet"
                  >
                    <path
                      stroke="rgb(215, 61, 50)"
                      id="outline"
                      fill="none"
                      stroke-width="4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-miterlimit="10"
                      d="M93.9,46.4c9.3,9.5,13.8,17.9,23.5,17.9s17.5-7.8,17.5-17.5s-7.8-17.6-17.5-17.5c-9.7,0.1-13.3,7.2-22.1,17.1 				c-8.9,8.8-15.7,17.9-25.4,17.9s-17.5-7.8-17.5-17.5s7.8-17.5,17.5-17.5S86.2,38.6,93.9,46.4z"
                    />
                    <path
                      id="outline-bg"
                      opacity="0.05"
                      fill="none"
                      stroke="#fff"
                      stroke-width="4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-miterlimit="10"
                      d="				M93.9,46.4c9.3,9.5,13.8,17.9,23.5,17.9s17.5-7.8,17.5-17.5s-7.8-17.6-17.5-17.5c-9.7,0.1-13.3,7.2-22.1,17.1 				c-8.9,8.8-15.7,17.9-25.4,17.9s-17.5-7.8-17.5-17.5s7.8-17.5,17.5-17.5S86.2,38.6,93.9,46.4z"
                    />
                  </svg>
                ) : (
                  <>
                    <ReactTooltip place="top" type="dark" effect="float" />
                    <Button
                      color="primary"
                      onClick={handleSubmit}
                      disabled={saveProcessImagesLoading}
                      style={{
                        width: "130px",
                        fontSize: "20px",
                        background: "#FF5252",
                        border: "#FF5252",
                      }}
                      className="btn-sm "
                    >
                      <AllInclusive />
                    </Button>
                  </>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="mt--8 mb-5" fluid>
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row>
                  <Col>
                    <h2>
                      Past Executions{" "}
                      {data && data.data.length ? `(${data.data.length})` : ""}
                    </h2>
                  </Col>
                </Row>
              </CardHeader>
              <Paper className="px-4">{content}</Paper>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ImageIntel;
